import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useVisualization } from '@/context';
import type { CarmAngles } from '@/visualization/domain/carm-angles';
import { CarmViewService } from '@/visualization/services/carm-view-service';
import type { CarmEditTarget } from '@/visualization/services/carm-view-service';

export interface CarmEditSnapshot {
    /** Index of the view being updated, or 'new' for a creation. */
    readonly target: CarmEditTarget;
    /** Current draft angles (always clamped into the C-arm envelope). */
    readonly angles: CarmAngles;
    readonly canCommit: boolean;
    /** Step one axis by a signed delta in degrees (the panel's chevron steppers). */
    readonly adjust: (axis: keyof CarmAngles, delta: number) => void;
    readonly confirm: () => void;
    readonly cancel: () => void;
}

/**
 * Thin bridge to the C-arm edit draft of CarmViewService.
 *
 * Returns null outside edit mode. Camera orchestration stays in
 * CarmCameraBinding — confirm() only broadcasts a commit request, which the
 * binding answers with the live camera state.
 */
export function useCarmEdit(): CarmEditSnapshot | null {
    const { getServiceManager } = useVisualization();
    const [snapshot, setSnapshot] = useState<CarmEditSnapshot | null>(null);
    const serviceRef = useRef<CarmViewService | null>(null);

    const actions = useMemo(
        () => ({
            adjust: (axis: keyof CarmAngles, delta: number) => {
                const service = serviceRef.current;
                const draft = service?.getDraft();
                if (!service || !draft) return;
                service.setDraftAngles({ ...draft.angles, [axis]: draft.angles[axis] + delta });
            },
            confirm: () => serviceRef.current?.requestCommitEdit(),
            cancel: () => serviceRef.current?.cancelEdit(),
        }),
        []
    );

    const buildSnapshot = useCallback(() => {
        const service = serviceRef.current;
        const draft = service?.getDraft();
        setSnapshot(
            draft && service
                ? {
                      target: draft.target,
                      angles: draft.angles,
                      canCommit: service.canCommitDraft(),
                      ...actions,
                  }
                : null
        );
    }, [actions]);

    useEffect(() => {
        const service = getServiceManager().getService<CarmViewService>(CarmViewService.SERVICE_NAME);
        serviceRef.current = service;

        buildSnapshot();

        const unsubs = [
            service.subscribe(service.EVENTS.EDIT_STARTED, buildSnapshot),
            service.subscribe(service.EVENTS.EDIT_CHANGED, buildSnapshot),
            service.subscribe(service.EVENTS.EDIT_COMMITTED, buildSnapshot),
            service.subscribe(service.EVENTS.EDIT_CANCELLED, buildSnapshot),
        ];

        return () => {
            serviceRef.current = null;
            unsubs.forEach((u) => u());
        };
    }, [getServiceManager, buildSnapshot]);

    return snapshot;
}
